/**
 * CreativeWorkspace — the Creative Workspace shell (epic #46, slice #B).
 *
 * One workspace per offer holds the ANGLE once; under it the operator opens
 * multiple BODIES/CONCEPTS, each its own lane with its own scoped research chat
 * and (image lane) its own generate → review → submit flow. Adding another body
 * under the same angle never restarts the session or loses the angle or any
 * sibling body's conversation — the capability the old `tracks` mechanism could
 * not provide.
 *
 * It reuses the PROVEN backend: the same angle services, the same
 * ComfyUI/FLUX.2 image pipeline, and the same approve-is-submit path validated
 * in PR #32. The workspace/body structure is the new foundation (#47); the
 * image generation under a body is the unchanged pipeline, with its job recorded
 * onto the body. Submit is approval — it never activates an ad or spends.
 *
 * Image lane first (this slice). Autonomous (#C) and Video (#D) render their own
 * per-body panel behind the same shell later; the lane tab is already modelled.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import ReferenceImagePanel from "./ReferenceImagePanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ax = { withCredentials: true };

const LANES = [
  { id: "image", label: "Image", ready: true },
  { id: "autonomous", label: "Autonomous", ready: true },
  { id: "video", label: "Custom Video", ready: true },
];

export default function CreativeWorkspace({ offerId, onBack } = {}) {
  const [workspace, setWorkspace] = useState(null);
  const [resumable, setResumable] = useState([]);
  const [choicePending, setChoicePending] = useState(false);
  const [error, setError] = useState(null);

  // Angle (shared across all bodies)
  const [angles, setAngles] = useState([]);
  const [genAngles, setGenAngles] = useState(false);
  const [ownAngle, setOwnAngle] = useState("");
  const [creatingAngle, setCreatingAngle] = useState(false);

  const [activeBodyId, setActiveBodyId] = useState(null);

  // ---- load / resume ----
  const loadWorkspaces = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/creative/workspaces`, { params: { offer_id: offerId }, ...ax });
      const list = r.data?.workspaces || [];
      setResumable(list);
      setChoicePending(list.length > 0);
    } catch (e) { setError(readErr(e)); }
  }, [offerId]);

  useEffect(() => { if (offerId) loadWorkspaces(); }, [offerId, loadWorkspaces]);

  const loadAngles = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/offers/${offerId}/angles`, ax);
      setAngles(r.data?.angles || r.data || []);
    } catch (e) { /* angle list is best-effort */ }
  }, [offerId]);

  useEffect(() => { if (offerId && !workspace && !choicePending) loadAngles(); },
    [offerId, workspace, choicePending, loadAngles]);

  const refreshWorkspace = useCallback(async (wid) => {
    const r = await axios.get(`${API}/creative/workspace/${wid}`, ax);
    setWorkspace(r.data);
    return r.data;
  }, []);

  async function resume(wid) {
    setChoicePending(false);
    try { const ws = await refreshWorkspace(wid); setActiveBodyId(ws.bodies?.[0]?.body_id || null); }
    catch (e) { setError(readErr(e)); }
  }

  async function startNew() { setChoicePending(false); setWorkspace(null); loadAngles(); }

  // ---- angle → create workspace ----
  async function generateAngles() {
    setGenAngles(true);
    try { await axios.post(`${API}/offers/${offerId}/angles/generate`, {}, ax); await loadAngles(); }
    catch (e) { setError(readErr(e)); } finally { setGenAngles(false); }
  }

  async function chooseAngle(angle) {
    setCreatingAngle(true);
    try {
      const r = await axios.post(`${API}/creative/workspace`, {
        offer_id: offerId, angle_id: angle.angle_id, angle_name: angle.angle_name,
        channel: "META", first_body_lane: "image", psychology_map_confirmed: true,
      }, ax);
      setWorkspace(r.data);
      setActiveBodyId(r.data.bodies?.[0]?.body_id || null);
    } catch (e) { setError(readErr(e)); } finally { setCreatingAngle(false); }
  }

  async function chooseOwnAngle() {
    if (!ownAngle.trim()) return;
    setCreatingAngle(true);
    try {
      const a = await axios.post(`${API}/offers/${offerId}/angles/custom-simple`,
        { description: ownAngle.trim() }, ax);
      await chooseAngle(a.data);
    } catch (e) { setError(readErr(e)); } finally { setCreatingAngle(false); }
  }

  // ---- bodies ----
  async function addBody(lane) {
    try {
      const r = await axios.post(`${API}/creative/workspace/${workspace.workspace_id}/bodies`,
        { lane }, ax);
      setWorkspace(r.data);
      const newest = r.data.bodies?.[r.data.bodies.length - 1];
      if (newest) setActiveBodyId(newest.body_id);
    } catch (e) { setError(readErr(e)); }
  }

  // ---------------------------------------------------------------- render
  if (error) return <Shell onBack={onBack}><Err msg={error} onClear={() => setError(null)} /></Shell>;

  if (choicePending) {
    return (
      <Shell onBack={onBack}>
        <Card title="Resume a workspace">
          <p style={sub}>You have {resumable.length} workspace(s) in progress for this offer.</p>
          {resumable.map((w) => (
            <button key={w.workspace_id} style={rowBtn} onClick={() => resume(w.workspace_id)}>
              {w.angle_name || "(no angle yet)"} · {(w.bodies || []).length} bod{(w.bodies||[]).length === 1 ? "y" : "ies"} · updated {fmt(w.updated_at)}
            </button>
          ))}
          <button style={ghostBtn} onClick={startNew}>Start a new workspace</button>
        </Card>
      </Shell>
    );
  }

  if (!workspace) {
    return (
      <Shell onBack={onBack}>
        <Card title="Choose an angle">
          <p style={sub}>The angle governs the whole workspace. Pick one, generate a set, or write your own.</p>
          <button style={ghostBtn} disabled={genAngles} onClick={generateAngles}>
            {genAngles ? "Generating…" : "Generate angles"}
          </button>
          <div style={{ marginTop: 12 }}>
            {angles.map((a) => (
              <div key={a.angle_id} style={angleRow}>
                <span>{a.angle_name}</span>
                <button style={miniBtn} disabled={creatingAngle} onClick={() => chooseAngle(a)}>Use this angle</button>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 16 }}>
            <textarea style={ta} placeholder="Or describe your own angle in a sentence…"
              value={ownAngle} onChange={(e) => setOwnAngle(e.target.value)} />
            <button style={miniBtn} disabled={creatingAngle || !ownAngle.trim()} onClick={chooseOwnAngle}>
              {creatingAngle ? "Creating…" : "Use my angle"}
            </button>
          </div>
        </Card>
      </Shell>
    );
  }

  const bodies = workspace.bodies || [];
  const activeBody = bodies.find((b) => b.body_id === activeBodyId) || bodies[0];

  return (
    <Shell onBack={onBack}>
      <Card title="Angle">
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>Working angle: <strong>{workspace.angle_name || "—"}</strong></span>
          <span style={sub}>{bodies.length} bod{bodies.length === 1 ? "y" : "ies"} under this angle</span>
        </div>
      </Card>

      <Card title="Bodies / Concepts">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
          {bodies.map((b) => (
            <button key={b.body_id}
              style={{ ...tab, ...(b.body_id === activeBody?.body_id ? tabActive : {}) }}
              onClick={() => setActiveBodyId(b.body_id)}>
              {b.lane} · {b.status === "submitted" ? "submitted" : "in progress"}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {LANES.map((l) => (
            <button key={l.id} style={miniBtn} disabled={!l.ready}
              title={l.ready ? "" : "Coming in a later slice"}
              onClick={() => l.ready && addBody(l.id)}>
              + {l.label} body{l.ready ? "" : " (soon)"}
            </button>
          ))}
        </div>
      </Card>

      {activeBody && (
        <BodyPanel key={activeBody.body_id} workspace={workspace} body={activeBody}
          onWorkspace={setWorkspace} onError={setError} refresh={refreshWorkspace} />
      )}
    </Shell>
  );
}

/** One body's lane panel. Image lane: scoped chat + brief + generate + review + submit. */
/** One body's panel: a scoped research chat shared by every lane, plus the
 *  lane-specific producer. Image lane: brief → generate → review → submit.
 *  Autonomous lane: hooks → select → script → submit the script as the
 *  body's artifact. Chat is the same endpoint and thread for both. */
function BodyPanel({ workspace, body, onWorkspace, onError, refresh }) {
  const wid = workspace.workspace_id;
  const [chat, setChat] = useState(body.chat_transcript || []);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  // Reset the visible thread only when the active body changes — intentionally
  // not on chat_transcript, which this component appends to locally.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setChat(body.chat_transcript || []); }, [body.body_id]);

  async function send() {
    if (!msg.trim()) return;
    const mine = msg.trim(); setMsg(""); setSending(true);
    setChat((c) => [...c, { role: "user", content: mine }]);
    try {
      const r = await axios.post(`${API}/creative/workspace/${wid}/bodies/${body.body_id}/chat`,
        { message: mine }, ax);
      setChat((c) => [...c, { role: "assistant", content: r.data.assistant, sources: r.data.sources }]);
    } catch (e) { onError(readErr(e)); } finally { setSending(false); }
  }

  return (
    <>
      <Card title={`Chat — ${body.lane} body`}>
        <div style={chatBox}>
          {chat.map((t, i) => (
            <div key={i} style={{ margin: "6px 0", textAlign: t.role === "user" ? "right" : "left" }}>
              <div style={t.role === "user" ? bubbleUser : bubbleAsst}>{t.content}</div>
              {(t.sources || []).length > 0 && (
                <div style={sourcesBox}>
                  {t.sources.map((s, j) => (<a key={j} href={s.url} target="_blank" rel="noreferrer" style={srcLink}>{s.title || s.url}</a>))}
                </div>
              )}
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input style={input} placeholder="Ask this body's chat… (its own thread)"
            value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} />
          <button style={miniBtn} disabled={sending} onClick={send}>{sending ? "…" : "Send"}</button>
        </div>
      </Card>

      {body.lane === "image" && (
        <ImageLane workspace={workspace} body={body} onWorkspace={onWorkspace} onError={onError} refresh={refresh} />
      )}
      {body.lane === "autonomous" && (
        <AutonomousLane workspace={workspace} body={body} onWorkspace={onWorkspace} onError={onError} refresh={refresh} />
      )}
      {body.lane === "video" && (
        <VideoLane workspace={workspace} body={body} onWorkspace={onWorkspace} onError={onError} refresh={refresh} />
      )}
    </>
  );
}

/** Image lane — brief → generate → review → submit. Unchanged behaviour from
 *  the validated slice #B; only lifted into its own component. */
function ImageLane({ workspace, body, onWorkspace, onError, refresh }) {
  const wid = workspace.workspace_id;
  const [brief, setBrief] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [job, setJob] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(body.status === "submitted");

  async function draftBrief() {
    setDrafting(true);
    try {
      const r = await axios.post(`${API}/creative/image/brief`,
        { offer_id: workspace.offer_id, channel: workspace.channel || "META", angle_id: workspace.angle_id }, ax);
      setBrief(r.data);
    } catch (e) { onError(readErr(e)); } finally { setDrafting(false); }
  }

  async function generate() {
    if (!brief) return;
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/creative/image/brief/${brief.brief_id}/generate`,
        { formats: ["META_SQUARE"] }, ax);
      const jobId = r.data.job_id;
      await axios.post(`${API}/creative/workspace/${wid}/bodies/${body.body_id}/jobs`, { job_id: jobId }, ax);
      let done = false;
      for (let i = 0; i < 50 && !done; i++) {
        await sleep(6000);
        const jr = await axios.get(`${API}/creative/image/jobs/${jobId}`, ax);
        setJob(jr.data);
        const cs = jr.data.creatives || [];
        if (cs.length && cs.every((c) => ["ready", "failed", "generated_pending_delivery", "approved"].includes(c.status))) done = true;
      }
    } catch (e) { onError(readErr(e)); } finally { setGenerating(false); }
  }

  async function submit() {
    if (!job || selected.length === 0) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/creative/workspace/${wid}/bodies/${body.body_id}/submit`,
        { job_id: job.job_id, creative_ids: selected }, ax);
      setSubmitted(true);
      const ws = await refresh(wid); onWorkspace(ws);
    } catch (e) { onError(readErr(e)); } finally { setSubmitting(false); }
  }

  const readyCreatives = (job?.creatives || []).filter((c) => c.status === "ready" || c.status === "approved");

  return (
    <>
      <Card title="Generate">
        {!brief && <button style={ghostBtn} disabled={drafting} onClick={draftBrief}>{drafting ? "Drafting brief…" : "Generate brief"}</button>}
        {brief && !job && (
          <>
            <p style={sub}>Brief ready. On-image: <strong>{brief.primary_headline}</strong> / CTA <strong>{brief.cta_text}</strong>.</p>
            <button style={ghostBtn} disabled={generating} onClick={generate}>{generating ? "Generating image…" : "Generate 1 image"}</button>
          </>
        )}
        {generating && <p style={sub}>Generating on the creative pod — a cold start can take up to ~5 minutes…</p>}
      </Card>

      <ReferenceImagePanel offerId={workspace.offer_id} />

      {readyCreatives.length > 0 && (
        <Card title="Review & submit">
          <p style={sub}>Submitting approves only what you check — it never activates an ad or authorizes spend.</p>
          {readyCreatives.map((c) => (
            <label key={c.creative_id} style={reviewRow}>
              <input type="checkbox" checked={selected.includes(c.creative_id)}
                onChange={(e) => setSelected((s) => e.target.checked ? [...s, c.creative_id] : s.filter((x) => x !== c.creative_id))} />
              <span>{c.creative_name || c.creative_id} · {c.format}</span>
              {c.drive_web_link && <a href={c.drive_web_link} target="_blank" rel="noreferrer" style={srcLink}>View in Drive →</a>}
              <NotMeasurable creativeId={c.creative_id} />
            </label>
          ))}
          <button style={ghostBtn} disabled={submitting || selected.length === 0 || submitted} onClick={submit}>
            {submitted ? "Submitted" : submitting ? "Submitting…" : `Submit ${selected.length} selected`}
          </button>
        </Card>
      )}

      {submitted && <NextBody />}
    </>
  );
}

/** Autonomous lane — the existing 5-step hook/script/CTA pipeline, entered per
 *  body and scoped by session_id = body_id, ending in submitting the chosen
 *  script as this body's artifact. Every generation call is the existing,
 *  unchanged service; only the entry point is the workspace. */
function AutonomousLane({ workspace, body, onWorkspace, onError, refresh }) {
  const wid = workspace.workspace_id;
  const offerId = workspace.offer_id;
  const angleId = workspace.angle_id;
  const sid = body.body_id; // per-body session scope for the pipeline

  const [hooks, setHooks] = useState([]);
  const [genHooks, setGenHooks] = useState(false);
  const [selHooks, setSelHooks] = useState([]);
  const [hooksLocked, setHooksLocked] = useState(false);
  const [lockingHooks, setLockingHooks] = useState(false);

  const [scripts, setScripts] = useState([]);
  const [genScripts, setGenScripts] = useState(false);
  const [chosen, setChosen] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(body.status === "submitted");
  const submittedArtifact = body.submitted_artifact_id || null;

  async function generateHooks() {
    setGenHooks(true);
    try {
      const r = await axios.post(`${API}/offers/${offerId}/hooks/generate`,
        { angle_id: angleId, session_id: sid }, ax);
      if (r.data.status === "error") { onError(r.data.error || "Hook generation failed"); return; }
      setHooks(r.data.hooks || []);
      setHooksLocked(false); setSelHooks([]);
    } catch (e) { onError(readErr(e)); } finally { setGenHooks(false); }
  }

  async function lockHooks() {
    if (selHooks.length === 0) return;
    setLockingHooks(true);
    try {
      await axios.post(`${API}/offers/${offerId}/hooks/select`,
        { hook_element_ids: selHooks, session_id: sid }, ax);
      setHooksLocked(true);
    } catch (e) { onError(readErr(e)); } finally { setLockingHooks(false); }
  }

  async function generateScripts() {
    setGenScripts(true);
    try {
      const r = await axios.post(`${API}/offers/${offerId}/scripts/generate`,
        { angle_id: angleId, session_id: sid, platform: "META", script_length: "30-45" }, ax);
      if (r.data.status === "error") { onError(r.data.error || "Script generation failed"); return; }
      setScripts(r.data.scripts || []);
    } catch (e) { onError(readErr(e)); } finally { setGenScripts(false); }
  }

  async function submitScript() {
    if (!chosen) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/creative/workspace/${wid}/bodies/${body.body_id}/submit-artifact`,
        { artifact_id: chosen }, ax);
      setSubmitted(true);
      const ws = await refresh(wid); onWorkspace(ws);
    } catch (e) { onError(readErr(e)); } finally { setSubmitting(false); }
  }

  if (submitted) {
    return (
      <>
        <Card title="Submitted">
          <p style={sub}>This autonomous body is submitted{submittedArtifact ? <> — script <code>{submittedArtifact}</code></> : null}. Submit is approval only; it did not activate an ad or authorize spend. The submitted script is routed into the existing script-to-production path, which generates the production brief and a Trello card for a human editor.</p>
        </Card>
        <NextBody />
      </>
    );
  }

  return (
    <>
      <Card title="1 · Hooks">
        <button style={ghostBtn} data-testid="ws-autonomous-gen-hooks" disabled={genHooks} onClick={generateHooks}>
          {genHooks ? "Generating hooks…" : hooks.length ? "Regenerate hooks" : "Generate hooks"}
        </button>
        {hooks.map((h) => (
          <label key={h.element_id} style={reviewRow}>
            <input type="checkbox" disabled={hooksLocked} checked={selHooks.includes(h.element_id)}
              onChange={(e) => setSelHooks((s) => e.target.checked ? [...s, h.element_id] : s.filter((x) => x !== h.element_id))} />
            <span><strong style={{ color: "#9db4e0" }}>{h.hook_type || "hook"}</strong> — {h.content}</span>
          </label>
        ))}
        {hooks.length > 0 && !hooksLocked && (
          <button style={miniBtn} data-testid="ws-autonomous-use-hooks" disabled={selHooks.length === 0 || lockingHooks} onClick={lockHooks}>
            {lockingHooks ? "Saving…" : `Use ${selHooks.length} selected hook${selHooks.length === 1 ? "" : "s"}`}
          </button>
        )}
        {hooksLocked && <p style={sub}>{selHooks.length} hook{selHooks.length === 1 ? "" : "s"} selected for this body.</p>}
      </Card>

      {hooksLocked && (
        <Card title="2 · Script">
          <button style={ghostBtn} data-testid="ws-autonomous-gen-scripts" disabled={genScripts} onClick={generateScripts}>
            {genScripts ? "Writing script(s)…" : scripts.length ? "Regenerate script(s)" : "Generate script(s)"}
          </button>
          {scripts.map((s) => (
            <div key={s.script_id} style={{ ...reviewRow, alignItems: "flex-start", flexDirection: "column" }}>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="radio" name={`script-${body.body_id}`} checked={chosen === s.script_id} onChange={() => setChosen(s.script_id)} />
                <span><code>{s.script_id}</code></span>
              </label>
              <div style={{ ...sub, whiteSpace: "pre-wrap", paddingLeft: 24 }}>
                {(s.sections || []).map((sec, i) => (
                  <div key={i} style={{ marginBottom: 4 }}>
                    <strong style={{ color: "#9db4e0" }}>{sec.section}{sec.timing ? ` (${sec.timing})` : ""}:</strong> {sec.spoken_text}
                    {sec.visual_direction ? <em style={{ color: "#6f86b0" }}> — {sec.visual_direction}</em> : null}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Card>
      )}

      {scripts.length > 0 && (
        <Card title="3 · Submit">
          <p style={sub}>Submitting records the chosen script as this body's artifact and locks the body. It never activates an ad, publishes, or authorizes spend — the existing production path handles delivery.</p>
          <button style={ghostBtn} data-testid="ws-autonomous-submit" disabled={submitting || !chosen} onClick={submitScript}>
            {submitting ? "Submitting…" : chosen ? "Submit selected script" : "Choose a script to submit"}
          </button>
        </Card>
      )}
    </>
  );
}

/** Custom Video lane — a structured production brief (scene / mood / hook /
 *  technical-spec) captured for a HUMAN editor, saved on the body, then
 *  submitted as the body's artifact. Submit is approval only: it NEVER fires
 *  Veo or ComfyUI (both stay refused), publishes, or authorizes spend.
 *
 *  On submit the server hands the brief to a human editor by creating a card
 *  on the Trello board (issues #61, #87) and stamps `trello_card_id` /
 *  `trello_card_url` back onto the body. That handoff is NON-BLOCKING server
 *  side: it can fail or be unconfigured while the submit still succeeds, and
 *  the body is stamped only on success. So the submitted state below reads the
 *  stamp rather than assuming it — a handoff that did not happen must not look
 *  like one that did (issue #88, CLAUDE.md §17.6). */
function VideoLane({ workspace, body, onWorkspace, onError, refresh }) {
  const wid = workspace.workspace_id;
  const saved = body.draft_brief && body.draft_brief.kind === "video_production_brief" ? body.draft_brief : null;

  const [scene, setScene] = useState(saved?.scene || "");
  const [mood, setMood] = useState(saved?.mood || "");
  const [hook, setHook] = useState(saved?.hook || "");
  const [aspectRatio, setAspectRatio] = useState(saved?.technical_spec?.aspect_ratio || "");
  const [duration, setDuration] = useState(saved?.technical_spec?.duration_seconds != null ? String(saved.technical_spec.duration_seconds) : "");
  const [notes, setNotes] = useState(saved?.technical_spec?.notes || "");

  const [briefId, setBriefId] = useState(body.current_brief_id || null);
  const [savingBrief, setSavingBrief] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(body.status === "submitted");
  const submittedArtifact = body.submitted_artifact_id || null;
  // Stamped by the server's non-blocking handoff, on success only (#61, #87).
  const trelloUrl = (body.trello_card_url || "").trim();
  const trelloCardId = (body.trello_card_id || "").trim();

  const hasContent = [scene, mood, hook, notes].some((v) => v.trim());

  async function saveBrief() {
    if (!hasContent) return;
    setSavingBrief(true);
    try {
      const r = await axios.post(`${API}/creative/workspace/${wid}/bodies/${body.body_id}/video-brief`,
        { scene: scene.trim(), mood: mood.trim(), hook: hook.trim(),
          aspect_ratio: aspectRatio.trim(),
          duration_seconds: duration.trim() ? Number(duration) : null,
          technical_notes: notes.trim() }, ax);
      setBriefId(r.data.brief_id);
      if (r.data.workspace) onWorkspace(r.data.workspace);
    } catch (e) { onError(readErr(e)); } finally { setSavingBrief(false); }
  }

  async function submitBrief() {
    if (!briefId) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/creative/workspace/${wid}/bodies/${body.body_id}/submit-artifact`,
        { artifact_id: briefId }, ax);
      setSubmitted(true);
      const ws = await refresh(wid); onWorkspace(ws);
    } catch (e) { onError(readErr(e)); } finally { setSubmitting(false); }
  }

  if (submitted) {
    return (
      <>
        <Card title="Submitted">
          <p style={sub}>This Custom Video body is submitted{submittedArtifact ? <> — production brief <code>{submittedArtifact}</code></> : null}. Submit is approval only; it did not fire Veo or ComfyUI (both refused), publish, or authorize spend.</p>
          {trelloUrl ? (
            <p style={sub} data-testid="ws-video-trello-handoff">Handed off to a human video editor on Trello — <a href={trelloUrl} target="_blank" rel="noreferrer" style={srcLink}>open the card →</a></p>
          ) : trelloCardId ? (
            <p style={sub} data-testid="ws-video-trello-handoff">Handed off to a human video editor on Trello — card <code>{trelloCardId}</code>. Trello returned no link for it, so there is nothing to open from here.</p>
          ) : (
            <p style={sub} data-testid="ws-video-trello-handoff">No Trello card is recorded against this brief, so the handoff to a human editor may not have completed. The submitted brief above is saved either way; the server log for this body says what happened.</p>
          )}
        </Card>
        <NextBody />
      </>
    );
  }

  return (
    <>
      <Card title="1 · Production brief">
        <p style={sub}>A structured brief for a human video editor — never auto-generated video. Fill what applies; you can revise and re-save before submitting.</p>
        <label style={fieldLabel}>Scene</label>
        <textarea style={ta} data-testid="ws-video-scene" placeholder="What happens on screen — setting, subject, action…"
          value={scene} onChange={(e) => setScene(e.target.value)} />
        <label style={fieldLabel}>Mood</label>
        <textarea style={ta} data-testid="ws-video-mood" placeholder="Tone and feel — energetic, calm, gritty, aspirational…"
          value={mood} onChange={(e) => setMood(e.target.value)} />
        <label style={fieldLabel}>Hook</label>
        <textarea style={ta} data-testid="ws-video-hook" placeholder="The opening beat that stops the scroll…"
          value={hook} onChange={(e) => setHook(e.target.value)} />
        <label style={fieldLabel}>Technical spec</label>
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <input style={input} data-testid="ws-video-aspect" placeholder="Aspect ratio (e.g. 9:16)"
            value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)} />
          <input style={input} data-testid="ws-video-duration" placeholder="Duration (seconds)" inputMode="numeric"
            value={duration} onChange={(e) => setDuration(e.target.value)} />
        </div>
        <textarea style={ta} data-testid="ws-video-notes" placeholder="Other technical direction — footage sources, captions, music, pacing…"
          value={notes} onChange={(e) => setNotes(e.target.value)} />
        <button style={ghostBtn} data-testid="ws-video-save-brief" disabled={savingBrief || !hasContent} onClick={saveBrief}>
          {savingBrief ? "Saving brief…" : briefId ? "Update brief" : "Save brief"}
        </button>
        {briefId && <p style={sub}>Brief saved as <code>{briefId}</code>.</p>}
      </Card>

      {briefId && (
        <Card title="2 · Submit">
          <p style={sub}>Submitting records this brief as the body's artifact and locks the body. It never fires Veo or ComfyUI, publishes, or authorizes spend — a human editor picks the brief up downstream.</p>
          <button style={ghostBtn} data-testid="ws-video-submit" disabled={submitting} onClick={submitBrief}>
            {submitting ? "Submitting…" : "Submit brief"}
          </button>
        </Card>
      )}
    </>
  );
}


/** Shared "you can keep going" footer after a body is submitted. */
function NextBody() {
  return (
    <Card title="Next">
      <p style={sub}>This body is submitted. The angle and workspace stay active — add another body above to keep going, without losing this conversation.</p>
    </Card>
  );
}

function NotMeasurable({ creativeId }) {
  const [state, setState] = useState(null);
  useEffect(() => {
    let live = true;
    axios.get(`${API}/creative/workspace/creative/${creativeId}/performance`, ax)
      .then((r) => { if (live) setState(r.data); }).catch(() => {});
    return () => { live = false; };
  }, [creativeId]);
  if (!state) return null;
  if (state.state === "MEASURED")
    return <span style={{ ...sub, color: "#7CFC9B" }}>spend ${state.spend} · ROAS {state.roas ?? "—"}</span>;
  return <span style={{ ...sub, fontStyle: "italic" }}>Not yet measurable</span>;
}

// ---- tiny presentational helpers (self-contained; no external design import) ----
const readErr = (e) => e?.response?.data?.detail || e?.message || "Request failed";
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

function Shell({ children, onBack }) {
  return (
    <div style={{ padding: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Creative Workspace</h2>
        {onBack && <button style={ghostBtn} onClick={onBack}>← Back</button>}
      </div>
      {children}
    </div>
  );
}
function Card({ title, children }) {
  return (<div style={card}><div style={cardTitle}>{title}</div>{children}</div>);
}
function Err({ msg, onClear }) {
  return (<div style={{ ...card, borderColor: "#c0392b" }}><strong>Something went wrong:</strong> {msg}<div><button style={miniBtn} onClick={onClear}>Dismiss</button></div></div>);
}

const card = { border: "1px solid #2a3550", borderRadius: 10, padding: 14, marginBottom: 12, background: "rgba(20,28,48,0.5)" };
const cardTitle = { fontWeight: 600, marginBottom: 8 };
const sub = { color: "#8aa0c8", fontSize: 13 };
const ghostBtn = { padding: "8px 14px", border: "1px solid #3a4a6a", borderRadius: 8, background: "#1d2740", color: "#dfe8ff", cursor: "pointer" };
const miniBtn = { padding: "6px 10px", border: "1px solid #3a4a6a", borderRadius: 6, background: "#243252", color: "#dfe8ff", cursor: "pointer" };
const rowBtn = { display: "block", width: "100%", textAlign: "left", padding: "10px 12px", margin: "6px 0", border: "1px solid #2a3550", borderRadius: 8, background: "#18213a", color: "#dfe8ff", cursor: "pointer" };
const angleRow = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", border: "1px solid #2a3550", borderRadius: 8, marginBottom: 6 };
const tab = { padding: "6px 10px", border: "1px solid #2a3550", borderRadius: 999, background: "#18213a", color: "#bcd", cursor: "pointer" };
const tabActive = { background: "#2f6df6", color: "#fff", borderColor: "#2f6df6" };
const ta = { width: "100%", minHeight: 60, background: "#0f1830", color: "#dfe8ff", border: "1px solid #2a3550", borderRadius: 8, padding: 8, marginBottom: 8 };
const fieldLabel = { display: "block", color: "#9db4e0", fontSize: 12, fontWeight: 600, margin: "6px 0 4px" };
const input = { flex: 1, background: "#0f1830", color: "#dfe8ff", border: "1px solid #2a3550", borderRadius: 8, padding: 8 };
const chatBox = { maxHeight: 260, overflowY: "auto", marginBottom: 8 };
const bubbleUser = { display: "inline-block", background: "#2f4bd6", color: "#fff", padding: "6px 10px", borderRadius: 10, maxWidth: "80%" };
const bubbleAsst = { display: "inline-block", background: "#1a2440", color: "#dfe8ff", padding: "6px 10px", borderRadius: 10, maxWidth: "85%", whiteSpace: "pre-wrap" };
const sourcesBox = { marginTop: 4 };
const srcLink = { color: "#7fb0ff", fontSize: 12, marginRight: 8 };
const reviewRow = { display: "flex", gap: 8, alignItems: "center", padding: "6px 0" };
